import { useState } from 'react';

import { FontAwesomeIcon } from '@appcharge/shared-ui';

const MAX_FORMATTED_ROWS = 100;

type JsonContainer = Record<string, unknown> | unknown[];
type ViewMode = 'formatted' | 'raw';

type MetadataDisplay =
  | {
      type: 'text';
      text: string;
      copyText: string;
    }
  | {
      type: 'json';
      entries: [string, unknown][];
      rawText: string;
      copyText: string;
    };

const isJsonContainer = (value: unknown): value is JsonContainer => {
  return typeof value === 'object' && value !== null;
};

const isEmptyContainer = (value: JsonContainer): boolean => {
  return Object.keys(value).length === 0;
};

const safeStringify = (
  value: unknown,
  fallback = '[Unable to display metadata value]'
): string => {
  try {
    return JSON.stringify(value, null, 2) ?? String(value);
  } catch {
    return fallback;
  }
};

const tryParseJsonContainer = (value: string): JsonContainer | null => {
  const trimmedValue = value.trim();

  if (!trimmedValue.startsWith('{') && !trimmedValue.startsWith('[')) {
    return null;
  }

  try {
    const parsedValue = JSON.parse(value);

    return isJsonContainer(parsedValue) ? parsedValue : null;
  } catch {
    return null;
  }
};

const createJsonDisplay = (
  value: JsonContainer,
  rawText?: string
): MetadataDisplay | null => {
  if (isEmptyContainer(value)) return null;

  const displayText = rawText ?? safeStringify(value);

  return {
    type: 'json',
    entries: Object.entries(value),
    rawText: displayText,
    copyText: displayText
  };
};

const getMetadataDisplay = (sessionMetadata: unknown): MetadataDisplay | null => {
  if (sessionMetadata == null) return null;

  if (typeof sessionMetadata === 'string') {
    const parsedValue = tryParseJsonContainer(sessionMetadata);

    if (parsedValue !== null) {
      return createJsonDisplay(parsedValue, sessionMetadata);
    }

    return {
      type: 'text',
      text: sessionMetadata,
      copyText: sessionMetadata
    };
  }

  if (isJsonContainer(sessionMetadata)) {
    return createJsonDisplay(sessionMetadata);
  }

  return {
    type: 'text',
    text: String(sessionMetadata),
    copyText: String(sessionMetadata)
  };
};

const formatPrimitiveValue = (value: unknown): string => {
  if (
    value == null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return String(value);
  }

  return safeStringify(value);
};

const CopyButton = ({
  value,
  label = 'Copy value'
}: {
  value: string;
  label?: string;
}) => {
  if (value === '') return null;

  return (
    <button
      aria-label={label}
      className="text-base-mutedForeground hover:text-base-foreground transition-colors shrink-0"
      onClick={() => {
        navigator.clipboard.writeText(value);
      }}
      title={label}
      type="button"
    >
      <FontAwesomeIcon icon="fa-regular fa-copy" />
    </button>
  );
};

const MetadataText = ({ children }: { children: string }) => (
  <pre
    className="m-0 max-h-96 overflow-auto whitespace-pre-wrap break-words font-sans text-sm text-base-primary"
    data-testid="orderMetadata"
  >
    {children}
  </pre>
);

const JsonValue = ({ value }: { value: unknown }) => {
  if (isJsonContainer(value)) {
    return (
      <pre className="m-0 max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-md bg-base-muted p-3 font-mono text-xs text-base-primary">
        {safeStringify(value)}
      </pre>
    );
  }

  return (
    <span className="whitespace-pre-wrap break-words text-sm text-base-mutedForeground">
      {formatPrimitiveValue(value)}
    </span>
  );
};

const ViewToggle = ({
  viewMode,
  onChange
}: {
  viewMode: ViewMode;
  onChange: (viewMode: ViewMode) => void;
}) => (
  <div className="flex rounded-md border border-base-sidebarBorder p-1">
    {(['formatted', 'raw'] as ViewMode[]).map((mode) => (
      <button
        aria-pressed={viewMode === mode}
        className={`rounded px-3 py-1 text-xs font-medium capitalize transition-colors ${
          viewMode === mode
            ? 'bg-base-primary text-white'
            : 'text-base-mutedForeground hover:text-base-primary'
        }`}
        key={mode}
        onClick={() => onChange(mode)}
        type="button"
      >
        {mode}
      </button>
    ))}
  </div>
);

const JsonRows = ({ entries }: { entries: [string, unknown][] }) => {
  const visibleEntries = entries.slice(0, MAX_FORMATTED_ROWS);
  const hiddenEntryCount = entries.length - visibleEntries.length;

  return (
    <div
      className="max-h-[480px] overflow-auto rounded-lg border border-base-sidebarBorder"
      data-testid="orderMetadata"
    >
      {visibleEntries.map(([key, value]) => (
        <div
          className="grid grid-cols-[minmax(140px,240px)_1fr] gap-4 border-b border-base-sidebarBorder px-4 py-3 last:border-b-0"
          key={key}
        >
          <div className="break-words text-sm font-medium text-base-foreground">
            {key}
          </div>
          <div className="flex min-w-0 items-start gap-2">
            <div className="min-w-0 flex-1">
              <JsonValue value={value} />
            </div>
            <CopyButton value={formatPrimitiveValue(value)} />
          </div>
        </div>
      ))}
      {hiddenEntryCount > 0 && (
        <div className="px-4 py-3 text-sm text-base-mutedForeground">
          Showing first {MAX_FORMATTED_ROWS} entries. Switch to Raw to view all{' '}
          {entries.length} entries.
        </div>
      )}
    </div>
  );
};

export const OrderMetadata = ({
  sessionMetadata
}: {
  sessionMetadata?: unknown;
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('formatted');
  const metadataDisplay = getMetadataDisplay(sessionMetadata);

  if (metadataDisplay == null) return null;

  const isJsonMetadata = metadataDisplay.type === 'json';

  return (
    <div className="flex flex-col gap-4 p-6 pb-8 border border-base-sidebarBorder rounded-lg">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <h2 className="font-medium text-xl text-base-primary">Metadata</h2>
          <CopyButton value={metadataDisplay.copyText} label="Copy metadata" />
        </div>
        {isJsonMetadata && (
          <ViewToggle viewMode={viewMode} onChange={setViewMode} />
        )}
      </div>

      {metadataDisplay.type === 'text' && (
        <MetadataText>{metadataDisplay.text}</MetadataText>
      )}

      {metadataDisplay.type === 'json' && viewMode === 'formatted' && (
        <JsonRows entries={metadataDisplay.entries} />
      )}

      {metadataDisplay.type === 'json' && viewMode === 'raw' && (
        <pre
          className="m-0 max-h-[480px] overflow-auto whitespace-pre-wrap break-words rounded-lg border border-base-sidebarBorder p-4 font-mono text-xs text-base-primary"
          data-testid="orderMetadata"
        >
          {metadataDisplay.rawText}
        </pre>
      )}
    </div>
  );
};
